import { useEffect } from 'react';

import { datadog } from '@sg/expo-datadog-client';
import { useToggleState } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useUsableNetActive } from '../useUsableNetActive/useUsableNetActive';

/**
 * Integrates UsableNet, our accessibility partner, with our web app.
 * If the feature flag is disabled, the script is removed from the DOM and the assistive mode is disabled.
 *
 * @see {@link https://drive.google.com/file/d/17NdCq73PH_IggrIFHcI5SA9688m4-y50/view}
 * @see {@link https://drive.google.com/file/d/1egYpajjkAB43gozqSFV3O9RZqzWT7xqS/view}
 * @see {@link https://www.figma.com/design/yVkGHKQqxIgvDztsay7Lnd/UsableNet-Integration?node-id=1-3642&t=6LqxCrHsPy4ZTaXS-0}
 */
export const useUsableNet = () => {
  const isEnabled = useFeatureFlag('cels-3985-usable-net-enabled', { listenForChanges: true }); // prettier-ignore
  const {
    value: isUsableNetReady,
    toggleOn: setIsUsableNetReady,
    toggleOff: setIsUsableNetNotReady,
  } = useToggleState(false);

  const { isActive } = useUsableNetActive();

  useEffect(() => {
    if (isEnabled) {
      addUsableNetScript(setIsUsableNetReady);

      return;
    }

    removeUsableNetScript(setIsUsableNetNotReady);
  }, [isEnabled, setIsUsableNetNotReady, setIsUsableNetReady]);

  useEffect(() => {
    void datadog.setGlobalAttribute(DATADOG_ATTR_KEY, isActive);
  }, [isActive]);

  return { isUsableNetReady };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const addUsableNetScript = (setIsUsableNetReady: () => void) => {
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.src = SCRIPT_SRC;
  script.async = true;
  script.dataset.rapid = 'true';
  script.addEventListener('load', () => {
    const usableNetWindow = window as unknown as UsableNetWindow;

    if (usableNetWindow.enableUsableNetAssistive) {
      setIsUsableNetReady();
    }
  });

  document.head.insertBefore(script, document.head.firstChild);
};

const removeUsableNetScript = (setIsUsableNetNotReady: () => void) => {
  setIsUsableNetNotReady();

  try {
    const usableNetWindow = window as unknown as UsableNetWindow;

    const isAssistiveModeEnabled = document.documentElement.classList.contains('un-assistive');

    if (isAssistiveModeEnabled && usableNetWindow.disableUsableNetAssistive) {
      usableNetWindow.disableUsableNetAssistive();
    }

    const existingScript = document.querySelector(`#${SCRIPT_ID}`);

    if (existingScript) {
      existingScript.remove();
    }
  } catch {}
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DATADOG_ATTR_KEY = 'isAccessibilityModeEnabled';
const SCRIPT_ID = 'usntA42start';
const SCRIPT_SRC = 'https://a42cdn.usablenet.com/a42/sweetgreen/default/prod/cs-text-start';

// ─── Types ───────────────────────────────────────────────────────────────────

type UsableNetWindow = {
  enableUsableNetAssistive?: () => void;
  disableUsableNetAssistive?: () => void;
};
